import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import AppConstants from "@utils/app.constants";
import api from "@common/services/api.service";
import { AuthStore, buildUserFromToken, User } from "@auth/domain/auth.config";
import {
  ApplicationResponse,
  StateTrackedData,
} from "@common/domain/common.model";

import { jwtDecode } from "jwt-decode";

const initialState: AuthStore = {
  status: "LOGGED_OUT",
  user: { error: null, status: "idle", data: undefined },
  token: { error: null, status: "idle", data: undefined },
  userInfo: { error: null, status: "loading", data: undefined },
};

export const login = createAsyncThunk<
  { user: User; token: string } | undefined,
  { username: string; password: string }
>("auth/login", async (payload) => {
  const response = await api.post<ApplicationResponse<any>>(
    `${AppConstants.api}/auth/login`,
    payload
  );
  if (response.data.data == null) { 
    return undefined}
  const token = response.data.data.token ?? "";
  const user: User = buildUserFromToken(jwtDecode(token));
  return { user, token };
});

export const fetchUserInfo = createAsyncThunk<any, { id: string }>(
  "auth/fetchUserInfo",
  async (id) => {
    const response = await api.get<ApplicationResponse<any>>(
      `${AppConstants.api}/user`,
      {
        params: {
          id,
        },
      }
    );

    if (response.data.data === null) return undefined;
    return response.data.data;
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    updateToken: (state, action) => {
        state.token.status = "idle";
        state.token.data = action.payload.token;
        state.user.data = action.payload.user;
        state.token.error = null;
    },
    logout: (state) => {
      state.status = "LOGGED_OUT";
      state.user.status = "idle";
      state.user.data = undefined;
      state.user.error = null;

      state.token.status = "idle";
      state.token.data = undefined;
      state.token.error = null;
    },
    addCreatorRole: (state) => {
      if (state.user.data) {
        state.user.data?.roles.push("CREATOR");
      }
    },
    removeCreatorRole: (state) => {
      if (state.user.data) {
        state.user.data.roles = state.user.data?.roles.filter(
          (role) => role !== "CREATOR"
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = "LOADING";
        state.user.status = "loading";
        state.token.status = "loading";
      })
      .addCase(login.fulfilled, (state, action) => {
        if (action.payload == undefined) {
          state.status = "LOGGED_OUT";
          state.user.status = "failed";
          state.user.error = "Invalid username or password";

          state.token.status = "failed";
          state.token.error = "Invalid username or password";
          return;
        }

        state.status = "LOGGED_IN";
        state.user.status = "idle";
        state.user.data = action.payload.user;

        state.token.status = "idle";
        state.token.data = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "LOGGED_OUT";
        state.user.status = "failed";
        state.user.error = action.error.message;

        state.token.status = "failed";
        state.token.error = action.error.message;
      });
    builder
      .addCase(fetchUserInfo.pending, (state) => {
        state.userInfo.status = "loading";
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        state.userInfo.status = "idle";
        state.userInfo.data = action.payload;
      })
      .addCase(fetchUserInfo.rejected, (state, action) => {
        state.userInfo.status = "failed";
        state.userInfo.error = action.error.message;
      });
  },
});

export const { logout, addCreatorRole, removeCreatorRole,updateToken } = authSlice.actions;

export default authSlice.reducer;
